/**
 * Settings -> Integrations: Cloudflare Browser Rendering.
 *
 * The credential every screenshot in this system is taken with, and until this
 * panel existed no CRM screen could even say whether it was there. That is why
 * "the brain came back with no screenshots" was unexplainable: the answer was
 * always one of four things - no account id, no token, the switch off, or the
 * day's allowance spent - and none of the four was visible anywhere.
 *
 * ⚠ THE CREDENTIAL IS SHARED. The same token drives the platform's template
 * preview thumbnails and the screenshots the WordPress import takes. Switching
 * it off here switches those off too, which is the first thing the form says.
 *
 * ⚠ THE ACCOUNT ID BOX IS NEVER PREFILLED FROM THE EFFECTIVE VALUE. With no id
 * of its own the server borrows R2's, because it is the same Cloudflare account.
 * Showing that borrowed id in the box and posting it back on the next save would
 * freeze it into an owned one, and it would then stop following R2 without
 * anybody having decided that. So the box shows only what is STORED and a note
 * says where the rest comes from.
 */
import { useEffect, useState } from "react";
import { AlertCircle, Camera, CheckCircle2, PlugZap, RefreshCw, Save } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import {
  getBrowserSettings,
  saveBrowserSettings,
  testBrowserSettings,
  type BrowserPlan,
  type BrowserSettings,
  type BrowserTestResult,
  type SnapshotFailure,
} from "../../../../lib/browserRendering";
import { Btn, StatusPill, fmtBytes } from "../../../ui";
import { ConnStatus, IntegrationCard, PanelLoader } from "../panel/IntegrationCard";
import { Field, Note, Pill, SaveRow, SecretPill, SelectPill, ToggleRow } from "../panel/fields";
import { BrowserCostPanel } from "./BrowserCostPanel";
import { fmtBrowserMs } from "./browserTime";

/**
 * Which sentence explains each refusal. A lookup rather than a chain of
 * ternaries, so a reason the server learns to report and this panel has not
 * learned to phrase yet is a missing i18n key rather than a blank line. Same
 * precedent as `MapsCard`.
 */
const FAIL_KEY: Record<SnapshotFailure, string> = {
  not_configured: "settings.browser.failNotConfigured",
  unsupported_provider: "settings.browser.failUnsupportedProvider",
  rate_limited: "settings.browser.failRateLimited",
  unauthorized: "settings.browser.failUnauthorized",
  timeout: "settings.browser.failTimeout",
  upstream_error: "settings.browser.failUpstreamError",
  empty_result: "settings.browser.failEmptyResult",
};

/** Where the account id in force came from, when it is not this form. */
const SOURCE_KEY: Record<string, string> = {
  r2: "settings.browser.accountFromR2",
  env: "settings.browser.accountFromEnv",
  none: "settings.browser.accountMissing",
};

const HOUR_MS = 3_600_000;

export function BrowserRenderingCard() {
  const t = useT();
  const [settings, setSettings] = useState<BrowserSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");

  const [accountId, setAccountId] = useState("");
  // The token never comes back from the server, so this box starts empty and an
  // empty box means "leave the stored one alone" - the same rule the register's
  // contract password follows, and the opposite of the account id above.
  const [apiToken, setApiToken] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [plan, setPlan] = useState<BrowserPlan>("free");

  const [testing, setTesting] = useState(false);
  const [test, setTest] = useState<BrowserTestResult | null>(null);

  function hydrate(data: BrowserSettings) {
    setSettings(data);
    setAccountId(data.accountId);
    setApiToken("");
    setEnabled(data.switchedOn);
    setPlan(data.plan);
  }

  useEffect(() => {
    getBrowserSettings()
      .then(hydrate)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaveState("saving");
    try {
      await saveBrowserSettings({
        accountId: accountId.trim(),
        ...(apiToken.trim() ? { apiToken: apiToken.trim() } : {}),
        enabled,
        plan,
      });
      hydrate(await getBrowserSettings());
      setSaveState("saved");
      setTimeout(() => setSaveState("idle"), 2500);
    } catch {
      setSaveState("error");
    }
  }

  /** Runs a real capture with the SAVED credential. See `testHint` on screen. */
  async function handleTest() {
    setTesting(true);
    setTest(null);
    try {
      setTest(await testBrowserSettings());
    } catch {
      // A transport failure is not a verdict about the token, so it is phrased
      // as an upstream problem rather than as a refusal.
      setTest({ ok: false, reason: "upstream_error" });
    } finally {
      setTesting(false);
    }
  }

  if (loading) return <PanelLoader />;

  const limits = settings?.limits;
  // The form no longer matches what the server is using. Worth saying out loud
  // beside the Test button, which reads the stored credential and not this form.
  const dirty =
    !!settings &&
    (accountId.trim() !== settings.accountId ||
      apiToken.trim() !== "" ||
      enabled !== settings.switchedOn ||
      plan !== settings.plan);

  const sourceNote =
    settings && settings.accountId === "" ? SOURCE_KEY[settings.accountIdSource] : undefined;

  return (
    <div className="space-y-5">
      <IntegrationCard
        icon={Camera}
        title={t("settings.browser.title")}
        desc={t("settings.browser.desc")}
        status={
          <ConnStatus
            ok={!!settings?.enabled}
            okLabel={t("settings.browser.statusOn")}
            offLabel={t("settings.browser.statusOff")}
          />
        }
      >
        <form onSubmit={handleSave} className="space-y-5">
          {/* First, because it is the fact most likely to surprise somebody who
              came here to switch off a CRM feature. */}
          <Note tone="warn">{t("settings.browser.shared")}</Note>

          <Field label={t("settings.browser.accountId")} hint={t("settings.browser.accountIdHint")}>
            <Pill
              value={accountId}
              onChange={setAccountId}
              placeholder={t("settings.browser.accountIdPlaceholder")}
              autoComplete="off"
              mono
            />
          </Field>

          {sourceNote ? (
            <Note tone={settings?.accountIdSource === "none" ? "bad" : "info"}>{t(sourceNote)}</Note>
          ) : null}

          <Field
            label={t("settings.browser.apiToken")}
            hint={
              settings?.apiTokenSet && !apiToken
                ? t("settings.browser.apiTokenKept")
                : t("settings.browser.apiTokenHint")
            }
          >
            <SecretPill
              value={apiToken}
              onChange={setApiToken}
              placeholder={
                settings?.apiTokenSet ? t("settings.browser.apiTokenPlaceholder") : ""
              }
              reveal
              showLabel={t("settings.browser.showToken")}
              hideLabel={t("settings.browser.hideToken")}
            />
          </Field>

          {settings?.tokenSource === "env" ? (
            <Note tone="warn">{t("settings.browser.tokenFromEnv")}</Note>
          ) : null}
          {settings && !settings.apiTokenSet ? (
            <Note tone="bad">{t("settings.browser.tokenMissing")}</Note>
          ) : null}

          <div className="border-t border-[var(--crm-hair)] pt-5">
            <ToggleRow
              label={t("settings.browser.enabled")}
              hint={t("settings.browser.enabledHint")}
              checked={enabled}
              onChange={setEnabled}
            />
          </div>

          <Field label={t("settings.browser.plan")} hint={t("settings.browser.planHint")}>
            <SelectPill value={plan} onChange={(v) => setPlan(v as BrowserPlan)}>
              <option value="free">{t("settings.browser.planFree")}</option>
              <option value="paid">{t("settings.browser.planPaid")}</option>
            </SelectPill>
          </Field>

          {limits ? (
            <Note tone="mut">
              {plan === "paid"
                ? t("settings.browser.planPaidNote", {
                    hours: Math.round(limits.paidIncludedMs / HOUR_MS),
                    rate: limits.paidOverageUsdPerHour.toFixed(2),
                    base: limits.paidBaseUsdPerMonth,
                  })
                : t("settings.browser.planFreeNote", {
                    minutes: Math.round(limits.freeDailyMs / 60_000),
                    spacing: Math.round(limits.freeMinCallSpacingMs / 1000),
                    browsers: limits.freeConcurrentBrowsers,
                  })}
            </Note>
          ) : null}

          {/* The honest sentence about the button below it. Cloudflare publishes
              no endpoint that validates a token without starting a browser, so
              the only real test is a real capture, and it comes out of the same
              allowance everything else draws on. */}
          <p className="crm-sub !mt-0 text-[12px]">{t("settings.browser.testHint")}</p>

          {test ? <TestReport t={t} test={test} /> : null}
          {dirty ? <Note tone="mut">{t("settings.browser.testUsesSaved")}</Note> : null}

          <SaveRow>
            <Btn type="submit" tone="dark" disabled={saveState === "saving"}>
              {saveState === "saving" ? (
                <RefreshCw className="size-3.5 animate-spin" />
              ) : (
                <Save className="size-3.5" />
              )}
              {saveState === "saving" ? t("settings.browser.saving") : t("settings.browser.save")}
            </Btn>
            <Btn onClick={handleTest} disabled={testing}>
              {testing ? (
                <RefreshCw className="size-3.5 animate-spin" />
              ) : (
                <PlugZap className="size-3.5" />
              )}
              {testing ? t("settings.browser.testing") : t("settings.browser.test")}
            </Btn>
            {saveState === "saved" ? (
              <StatusPill tone="ok">{t("settings.browser.saved")}</StatusPill>
            ) : null}
            {saveState === "error" ? (
              <StatusPill tone="lt">{t("settings.browser.saveError")}</StatusPill>
            ) : null}
          </SaveRow>
        </form>
      </IntegrationCard>

      {limits ? <BrowserCostPanel limits={limits} /> : null}
    </div>
  );
}

/**
 * What the capture came back with.
 *
 * A block rather than a line beside the buttons: the useful part of a refusal is
 * which of the four possible causes it was, and that does not fit next to two
 * buttons.
 */
function TestReport({ t, test }: { t: ReturnType<typeof useT>; test: BrowserTestResult }) {
  const Icon = test.ok ? CheckCircle2 : AlertCircle;
  const bytes = test.bytes ?? 0;

  return (
    <Note tone={test.ok ? "ok" : "bad"} icon={<Icon className="mt-0.5 size-4 shrink-0" />} live>
      <span className="block font-[650]">
        {test.ok
          ? t("settings.browser.testOk")
          : t(test.reason ? FAIL_KEY[test.reason] : "settings.browser.failUnknown")}
      </span>

      {test.ok ? (
        <span className="mt-1 block font-mono text-[11.5px] opacity-70">
          {/* A capture with no reported duration still happened and still spent
              allowance, so the size is stated on its own rather than beside a
              zero that would read as free. */}
          {typeof test.browserMs === "number"
            ? t("settings.browser.testMeta", {
                ms: fmtBrowserMs(test.browserMs),
                size: fmtBytes(bytes),
              })
            : t("settings.browser.testMetaNoTime", { size: fmtBytes(bytes) })}
        </span>
      ) : null}
    </Note>
  );
}
